import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay, finalize } from 'rxjs/operators';
import { ApiService } from './api.service';
import { environment } from '../../../environments/environment';

export interface PrismResponse {
  tile: string;
  data: any;
  query: any;
}

@Injectable({
  providedIn: 'root'
})
export class PrismService {
  private apiService = inject(ApiService);
  private inFlightStreams = new Map<string, Observable<PrismResponse>>();

  /**
   * Fetches Prism dashboard data using SSE streaming.
   * Emits each tile individually as it arrives.
   * Deduplicates concurrent requests with the same parameters.
   */
  getPrismDataStream(startTime: string, endTime: string, unit: string): Observable<PrismResponse> {
    const params = new URLSearchParams({
      start_time: startTime,
      end_time: endTime,
      unit: unit,
    });

    const cacheKey = `${startTime}:${endTime}:${unit}`;
    const existing = this.inFlightStreams.get(cacheKey);
    if (existing) {
      return existing;
    }

    const url = `https://staging.practina.com/v1/api/prism?${params.toString()}`;
    const headers: Record<string, string> = {
      'Accept': 'text/event-stream',
    };

    const stream$ = new Observable<PrismResponse>(observer => {
      const controller = new AbortController();

      fetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal,
      })
        .then(async response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const reader = response.body?.getReader();
          const decoder = new TextDecoder();
          if (!reader) throw new Error('Response body is not readable');

          let buffer = '';

          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                const trimmedLine = line.trim();
                if (trimmedLine.startsWith('data:')) {
                  const dataStr = trimmedLine.substring(5).trim();
                  if (dataStr === '[DONE]') continue;

                  try {
                    const parsed = JSON.parse(dataStr);
                    if (parsed) observer.next(parsed);
                  } catch (e) {
                    console.error('Error parsing Prism SSE chunk:', e, dataStr);
                  }
                }
              }
            }
            observer.complete();
          } catch (e) {
            observer.error(e);
          }
        })
        .catch(err => {
          observer.error(err);
        });

      return () => controller.abort();
    }).pipe(
      shareReplay({ bufferSize: 100, refCount: true }),
      finalize(() => {
        this.inFlightStreams.delete(cacheKey);
      })
    );

    this.inFlightStreams.set(cacheKey, stream$);
    return stream$;
  }

  private getCsrfToken(): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'prism-auth_csrf_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  }
}
